// 11 §11.1 AuthoringStore — wraps the §7 state machine, holds the optimistic `local` map (§11.3),
// and owns the rAF-coalesced `recompute()` (§6.3) that calls SimBridge.project(). Server intents
// come from the encounter controller (decoded `intent` rows); reducers are the module's.
import { describeSquare, thingAt } from '../matter/matter';
import { create } from 'zustand';
import type { ActionId, Intent, IntentState, LegalityVerdict, ProjectedBoard, Slot, Snapshot, StandAt, Survey, Tile, UnitState } from '../sim/types';
import { fieldClassAt } from '../sim/field';
import { SLOTS, intentKey, neverSeen, tileEq, unknownSquare } from '../sim/types';
import type { SnapshotHandle } from '../sim/SimBridge';
import type { WasmSimBridge } from '../sim/WasmSimBridge';
import { AuthoringMachine, type AuthoringContext, type AuthoringOutput, type AuthoringState, type Probe, type Suggestion } from '../authoring/stateMachine';
import { CHANGE_GEAR, actionById, actionsForHero, type ActionDef } from '../sim/actions';
import { consumable } from '../sim/items';
import { slotToU8 } from '../sim/adapt';
import { OFFLINE_ERROR, rpc, useToasts } from '../net/reducers';
import type { ServerIntent } from '../encounter/session';
import { deviceNow } from '../time';
import { linkUp } from './link';
import { usePlayback } from './playback';
import { playSfx } from '../audio';
import { HIGH_GROUND_WORDS, heightOf } from '../../game/board/edges';
import { statusesLine } from '../sim/statuses';
import { covers } from '../sim/body';
import { HOTBAR_MORE, isHotkeyRelease } from '../input/hotkeys';
import { knownSnareAt, snareGuise } from '../snares/snares';

export interface IntentRow extends Intent { state: IntentState; owner: string; rowId?: bigint }

export interface AuthoringStore {
  snapshot?: SnapshotHandle; snapshotHash?: string; pulseIndex?: number; encounterId?: bigint;
  selectedHero?: number; slot?: Slot; action?: ActionId; pendingArgs: Partial<{ tile: Tile; unit: number; dir: string }>;
  local: Map<string, Intent>;                      // key `${heroId}:${slot}`, unsent or in flight
  rejected: Map<string, { intent: Intent; reason: string }>;
  projected?: ProjectedBoard; projectedAt: number; dirty: boolean;
  select(heroId?: number): void; chooseSlot(s: Slot): void; chooseAction(a: ActionId): void;
  setArg(k: string, v: unknown): LegalityVerdict; place(): void; clear(heroId: number, slot: Slot): void;
  recompute(): void;                               // rAF-coalesced

  // shell-internal surface
  machineState: AuthoringState;
  cursor: Tile;
  probe?: Probe;
  /** docs/33 E1: what a click at the cursor would do (no action chosen). */
  suggestion?: Suggestion;
  /**
   * The turn opened with the player's only hero picked for them: the board keeps its walk field
   * hidden until they ask for it (Move, a click, another hero). Cleared by anything they do.
   */
  autoSelected: boolean;
  hotbar: ActionDef[];
  announcement: string;
  me: string;
  controlled: number[];                            // hero ids I control
  /** Server rows of the current pulse keyed `${heroId}:${slot}` (decoded), for the HUD. */
  rows: Map<string, IntentRow>;
  /** docs/149 MP: the walks under way, each as the squares it takes per pulse. */
  routes: RouteTag[];
  /** docs/81 SA5: locked actions the server just took back (another player's change ruled them out); `seq` counts batches. */
  struck: { seq: number; items: Array<{ heroId: number; slot: Slot }> };
  attach(bridge: WasmSimBridge, handle: SnapshotHandle, snapshot: Snapshot, me: string, controlled: number[], encounterId: bigint, snapshotHashBytes: Uint8Array): void;
  detach(): void;
  setControlled(controlled: number[]): void;
  serverChanged(intents: ServerIntent[]): void;
  serverRoutes(routes: ServerRoute[]): void;
  send(ev: Parameters<AuthoringMachine['send']>[0]): void;
  hoverTile(tile: Tile): void;
  clickTile(tile: Tile): void;
  /** The selected hero's whole hotbar (both action slots plus bonus-only extras, Hold last) with its number keys. */
  barActions(): ActionDef[];
  /** docs/33 E3: a hotbar action (tile or number key) — into the open slot, else the first free slot that holds it. */
  pickAction(id: ActionId): void;
  /** The slot `pickAction(id)` would open for the selected hero: the open slot if it fits, else the first empty one that may hold it. */
  slotFor(id: ActionId): Slot | undefined;
  /** docs/40 IU7: Use one of the selected hero's bag rows (the inventory's Use button); a Salve then asks whom. */
  useItem(item: number): void;
  /** docs/83 PO8: put Change Gear in `heroId`'s Bonus slot (the inventory's planned moves ride on it). */
  changeGear(heroId: number): void;
  /** docs/40 IU8: whether Use (on the user itself) would be legal now, for the inventory's button and its reason. */
  itemVerdict(item: number): LegalityVerdict;
  mergedIntents(): IntentRow[];
  snapshotData(): Snapshot | undefined;
  stats(): { lastProjectMs: number; p95ProjectMs: number; calls: number } | undefined;
}

/** docs/149 MP: a walk the server holds for a hero (`hero_route`): where it is going. */
export interface ServerRoute { heroId: number; x: number; y: number }
/** The walk as the board draws it: the squares it takes in each pulse from now (leg 0 is this pulse). */
export interface RouteTag { heroId: number; legs: Tile[][] }

let bridge: WasmSimBridge | undefined;
let machine: AuthoringMachine | undefined;
let rafHandle: number | undefined;
let snapshotData: Snapshot | undefined;
let revisionSeq = 1;
let serverIntents: ServerIntent[] = [];
let routeRows: ServerRoute[] = [];
/** docs/54 RC5: intents placed while the link was down, sent by `resendUnsent` once it is back. */
const unsent = new Map<string, { bytes: Uint8Array; intent: Intent }>();
let resend: () => void = () => {};
/** Send the cards placed while offline whose pulse is still open (called after the catch-up). */
export function resendUnsent(): void { if (unsent.size && linkUp()) resend(); }
/** docs/33 E3: the hero's whole bar — action-slot list, then bonus-only extras, Hold on 0 — numbered in that order. */
function barFor(unit: UnitState | undefined, any = true): ActionDef[] {
  if (!any) return [];
  const a1 = actionsForHero(unit?.abilities, 1, unit, snapshotData?.units);
  const extra = actionsForHero(unit?.abilities, 'B', unit, snapshotData?.units).filter((b) => !a1.some((a) => a.id === b.id));
  const hold = a1.find((a) => a.id === 2);
  // 1–9, then Shift+1–9 (docs/87 `HOTBAR_MORE`) for a bar longer than the number row
  const keys = [...'123456789', ...HOTBAR_MORE];
  return [...a1.filter((a) => a.id !== 2), ...extra.filter((a) => a.id !== 2)].map((a, i) => ({ ...a, hotkey: keys[i] ?? '' })).concat(hold ? [hold] : []);
}

/** One slot's actions, keyed as on the whole bar so a number always means the same action. */
function slotBar(unit: UnitState | undefined, slot: Slot): ActionDef[] {
  const keys = new Map(barFor(unit).map((a) => [a.id, a.hotkey]));
  return actionsForHero(unit?.abilities, slot, unit, snapshotData?.units).map((a) => ({ ...a, hotkey: keys.get(a.id) ?? a.hotkey }));
}

let stopsCache: { key: string; stops: Array<{ tile: Tile; cost: number }> } | undefined;
/** docs/81 SA3: every unit's square as (hero, slot) begins, per plan. */
let standsCache = new Map<string, StandAt[]>();
let groundCache: { key: string; field: Survey | undefined } | undefined;
/** What a kit draws for a wall square, where the rules' land may not say (`fk_sim::Class::label`'s words). */
const WALL_DRAWN: Record<string, string> = { forest: 'trees', autumn: 'trees', pinewood: 'trees', winterwood: 'trees', jungle: 'trees', mirewood: 'reed beds', dunes: 'crags', highland: 'crags', underdark: 'pillars and giant fungi', ashlands: 'black crags' };
/** docs/41: Shift is held (a click on a block drags it instead of pushing). */
let dragHeld = false;

const schedule = (fn: () => void): number => (typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : (setTimeout(fn, 0) as unknown as number));
const cancel = (h: number): void => (typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame(h) : clearTimeout(h));

/** Server rows of the current pulse for this snapshot hash, keyed by (hero, slot). */
function currentRows(hash: string | undefined, pulseIndex: number | undefined): Map<string, IntentRow> {
  const out = new Map<string, IntentRow>();
  for (const r of serverIntents) {
    if (hash !== undefined && r.snapshotHash !== hash) continue;
    if (pulseIndex !== undefined && r.pulseIndex !== pulseIndex) continue;
    const key = `${r.heroId}:${r.intent.slot}`;
    const prev = out.get(key);
    if (prev && prev.revision > r.revision) continue;
    out.set(key, { ...r.intent, state: r.committed ? 'committed' : 'provisional', owner: r.owner, rowId: r.rowId });
  }
  return out;
}

/**
 * docs/81 SA5: the locked actions a server change took back — a committed action row that came
 * back as the substituted Hold, still committed (the plan stays locked; the action is gone).
 */
export function struckRows(prev: ReadonlyMap<string, IntentRow>, next: ReadonlyMap<string, IntentRow>): Array<{ heroId: number; slot: Slot }> {
  const out: Array<{ heroId: number; slot: Slot }> = [];
  for (const [k, n] of next) {
    const p = prev.get(k);
    if (!p || p.state !== 'committed' || p.substituted || p.action === HOLD_ID) continue;
    if (n.state === 'committed' && n.substituted && n.action === HOLD_ID) out.push({ heroId: n.heroId, slot: n.slot });
  }
  return out;
}
const HOLD_ID = 2;

/** The sim does not know who committed what: overlay row states onto the projected refs (11 §5.4). */
export function applyStates(board: ProjectedBoard, states: Map<string, IntentState>): ProjectedBoard {
  const fix = <T extends { heroId: number; slot: Slot; state: IntentState }>(ref: T): T => { const s = states.get(intentKey(ref)); return s ? { ...ref, state: s } : ref; };
  return {
    ...board,
    // Projection fills every empty slot with a Hold; only draw holds that exist as a row or a local edit (11 §5.2 neutral = an explicit Hold).
    // docs/81 SA7: a held bonus is no plan at all — the board shows a bonus only when one is planned
    effects: board.effects.filter((e) => e.kind !== 'neutral' || (states.has(intentKey(e.id)) && e.id.slot !== 'B')).map((e) => ({ ...e, id: fix(e.id) }) as typeof e),
    conflicts: board.conflicts.map((c) => ({ ...c, intent: fix(c.intent) })),
    ordering: board.ordering.map((o) => ({ ...o, first: fix(o.first), then: fix(o.then) })),
  };
}

export const useAuthoring = create<AuthoringStore>()((set, get) => {
  const syncFromMachine = () => {
    if (!machine) return;
    const st = machine.state;
    const heroId = st.kind === 'Idle' ? undefined : st.heroId;
    const slot = 'slot' in st ? st.slot : undefined;
    const action = st.kind === 'ActionChosen' ? st.action.id : undefined;
    const hero = heroId === undefined ? undefined : snapshotData?.units.find((u) => u.heroId === heroId);
    const patch: Partial<AuthoringStore> = {
      machineState: st, cursor: { ...machine.cursor }, hotbar: heroId !== undefined && slot !== undefined ? slotBar(hero, slot) : [],
      pendingArgs: st.kind === 'ActionChosen' ? { ...st.pending } : {},
    };
    patch.selectedHero = heroId;
    patch.slot = slot;
    patch.action = action;
    patch.probe = machine.probe;
    patch.suggestion = withHit(machine.suggestion);
    set(patch);
  };

  /** docs/33 E1: an attack suggestion carries what it would do to the foe, from a projection aside. */
  let hitCache: { from: Suggestion; to: Suggestion } | undefined;
  const withHit = (sug: Suggestion | undefined): Suggestion | undefined => {
    if (!sug || sug.kind !== 'plan' || sug.target === undefined || !bridge || !get().snapshot) return sug;
    if (hitCache?.from === sug) return hitCache.to;
    let to: Suggestion = sug;
    try {
      const drop = new Set(sug.intents.map(intentKey));
      const merged = get().mergedIntents().filter((i) => !drop.has(intentKey(i))).map(({ state: _s, owner: _o, rowId: _r, ...i }) => i);
      const board = bridge.projectAside(get().snapshot!, [...merged, ...sug.intents]);
      const key = intentKey(sug.intents[sug.intents.length - 1]!);
      const e = board.effects.find((x) => intentKey(x.id) === key);
      const u = board.units.find((x) => x.unitId === sug.target);
      if (e && e.kind === 'attack' && u) to = { ...sug, hit: { damage: e.damage, hpStart: u.hpStart, hpEnd: u.hpEnd, drops: u.dead } };
    } catch { /* the preview is a courtesy */ }
    hitCache = { from: sug, to };
    return to;
  };

  const handleOutput = (out: AuthoringOutput) => {
    const { encounterId, snapshot } = get();
    switch (out.type) {
      case 'place': {
        if (!bridge || !snapshot || encounterId === undefined) return;
        // Revision must increase per (hero, slot) within the pulse (04 §5.3): beat the server's and our own counter.
        const key = intentKey(out.intent);
        const existing = get().rows.get(key)?.revision ?? 0;
        const revision = Math.max(existing, revisionSeq) + 1;
        revisionSeq = revision;
        let made: ReturnType<WasmSimBridge['makeIntent']>;
        try { made = bridge.makeIntent(snapshot, out.intent.heroId, out.intent.slot, out.intent.action, out.intent.args, revision); }
        catch (err) { useToasts.getState().push(`intent: ${err instanceof Error ? err.message : String(err)}`, 'error'); return; }
        const local = new Map(get().local);
        local.set(key, made.intent);
        const rejected = new Map(get().rejected); rejected.delete(key);
        set({ local, rejected, dirty: true });
        get().recompute();
        playSfx('plan.place');   // docs/34: the card goes down
        // docs/54 RC5: with the link down the card stays on the board and goes out once it is back
        if (!linkUp()) {
          unsent.set(key, { bytes: made.bytes, intent: made.intent });
          set({ announcement: 'Kept on your board — it goes to the World when the connection is back' });
          break;
        }
        submit(encounterId, key, made.bytes, made.intent);
        break;
      }
      case 'clear': {
        const key = `${out.heroId}:${out.slot}`;
        const local = new Map(get().local); local.delete(key);
        const rejected = new Map(get().rejected); rejected.delete(key);
        set({ local, rejected, dirty: true });
        if (encounterId !== undefined && get().rows.has(key)) void rpc.withdrawIntent(encounterId, BigInt(out.heroId), slotToU8(out.slot));
        playSfx('plan.clear');
        get().recompute();
        break;
      }
      case 'route': {
        if (encounterId === undefined) return;
        playSfx('plan.place');
        void rpc.planRoute(encounterId, BigInt(out.heroId), out.tile.x, out.tile.y).then((r) => {
          if (r.ok) return;
          playSfx('ui.error');
          useToasts.getState().push(`walk: ${r.error}`, 'error');
        });
        break;
      }
      case 'reset': set({ local: new Map(), rejected: new Map(), dirty: true }); get().recompute(); break;
      case 'announce': set({ announcement: out.text }); break;
    }
  };

  // docs/41: Shift held (the `board.dragBlock` hotkey, docs/87) turns a click on a block from a push into a drag; the chip follows it
  if (typeof window !== 'undefined') {
    const shift = (on: boolean) => (e: KeyboardEvent) => {
      if (!isHotkeyRelease(e, 'board.dragBlock') || dragHeld === on) return;
      dragHeld = on;
      machine?.invalidate();
      set({ dirty: true });
    };
    window.addEventListener('keydown', shift(true));
    window.addEventListener('keyup', shift(false));
    window.addEventListener('blur', () => { dragHeld = false; });
  }
  const submit = (encounterId: bigint, key: string, bytes: Uint8Array, intent: Intent) => {
    void rpc.submitIntent(encounterId, bytes).then((r) => {
      if (r.ok) return;
      if (r.error === OFFLINE_ERROR) { unsent.set(key, { bytes, intent }); return; }   // RC5: dropped in flight, sent again
      playSfx('ui.error');
      const l = new Map(get().local);
      if (l.get(key)?.revision === intent.revision) l.delete(key);
      const rej = new Map(get().rejected); rej.set(key, { intent, reason: r.error });
      set({ local: l, rejected: rej, dirty: true, announcement: `${intent.heroId}: rejected — ${r.error}` });
      get().recompute();
    });
  };
  resend = () => {
    const encounterId = get().encounterId;
    const held = [...unsent];
    unsent.clear();
    if (encounterId === undefined) return;
    // only cards still on the board as placed (a later edit or a new pulse replaced the rest)
    for (const [key, u] of held) if (get().local.get(key)?.revision === u.intent.revision) submit(encounterId, key, u.bytes, u.intent);
  };
  const buildMachine = (snapshot: Snapshot, controlled: number[], keepHero?: number) => {
    const ctx: AuthoringContext = {
      heroes: () => snapshot.units.filter((u) => u.heroId !== undefined && controlled.includes(u.heroId) && u.onBoard !== false && !u.downed)
        .map((u) => ({ heroId: u.heroId!, unitId: u.unitId, classId: u.classId ?? '', tile: u.tile, name: u.name, ...(u.size && u.size > 1 ? { size: u.size } : {}) })),
      actions: (heroId, slot) => { const u = snapshot.units.find((x) => x.heroId === heroId); return actionsForHero(u?.abilities, slot, u, snapshot.units); },
      legal: (intent) => (bridge && get().snapshot ? bridge.legal(get().snapshot!, intent) : { legal: false, reason: 'Encounter' }),
      // docs/79: any square a huge body covers means that unit
      unitAt: (tile) => snapshot.units.find((u) => covers(u, tile) && u.onBoard !== false)?.unitId,
      // docs/126: a fallen foe's pouch is no stash to open: it is taken by walking onto it
      stashAt: (tile) => snapshot.stashes?.find((f) => f.kind !== 'Pouch' && f.kind !== 'Find' && tileEq(f.tile, tile) && !f.hidden && !f.opened)?.id,
      // docs/138 XO7: a shown find is picked up underfoot like a pouch
      pouchAt: (tile) => !!snapshot.stashes?.some((f) => (f.kind === 'Pouch' || (f.kind === 'Find' && !f.hidden)) && tileEq(f.tile, tile) && !f.opened),
      findAt: (tile) => !!snapshot.stashes?.some((f) => f.kind === 'Find' && !f.hidden && tileEq(f.tile, tile) && !f.opened),
      // a landmark (docs/50) is a name on the ground: whatever lies on it is what a click means
      // docs/106: a seat is floor under whatever stands on it
      fixtureAt: (tile) => {
        const here = (snapshot.fixtures ?? []).filter((f) => f.kind !== 'Landmark' && tileEq(f.tile, tile));
        return here.find((f) => f.kind !== 'Seat') ?? here[0];
      },
      // docs/140 DT11: a found, armed plate or wire of a dungeon trap
      snareAt: (tile) => { const s = knownSnareAt(snapshot.crawl, tile); return s ? { id: s.id, name: snareGuise(s), wire: s.trigger.kind === 'Wire' } : undefined; },
      // docs/114 MT4: a thing on the field (a hanging chandelier is clicked through to its square's unit)
      thingAt: (tile) => thingAt(snapshot, tile),
      dragMode: () => dragHeld,
      snapshotHash: () => snapshot.snapshotHash,
      filled: (heroId, slot) => get().local.has(`${heroId}:${slot}`) || get().rows.has(`${heroId}:${slot}`),
      committed: (heroId) => [...get().rows.values()].some((i) => i.heroId === heroId && i.state === 'committed'),
      sideOf: (unit) => { const u = snapshot.units.find((x) => x.unitId === unit); return !u ? undefined : u.faction === 'enemy' ? 'foe' : 'friend'; },
      unitName: (unit) => snapshot.units.find((x) => x.unitId === unit)?.name ?? 'the foe',
      // docs/69: what it carries — "Goblin · Staggered (this turn), Bleeding (2 turns)"
      unitNote: (unit) => {
        const u = snapshot.units.find((x) => x.unitId === unit);
        return statusesLine(u?.statuses ?? u?.marks?.map((kind) => ({ kind, magnitude: 0 })), (id) => snapshot.units.find((x) => x.unitId === id)?.name);
      },
      legalAfter: (intent, before) => (bridge && get().snapshot ? bridge.legalWith(get().snapshot!, intent, before) : { legal: false, reason: 'Encounter' }),
      // docs/149 MP: a walk to `tile` over the pulses to come, if there is one
      routeTo: (heroId, tile) => {
        const h = get().snapshot;
        const unit = snapshot.units.find((u) => u.heroId === heroId)?.unitId;
        return !!bridge && !!h && unit !== undefined && (bridge.routeLegs(h, unit, tile)?.length ?? 0) > 0;
      },
      stops: (heroId, slot) => {
        const h = get().snapshot;
        const unit = snapshot.units.find((u) => u.heroId === heroId)?.unitId;
        if (!bridge || !h || unit === undefined) return [];
        const key = `${h.id}:${h.hash}:${get().projected?.planHash ?? ''}:${heroId}:${slot}`;
        if (stopsCache?.key !== key) {
          const f = bridge.survey(h, unit, slot);
          stopsCache = { key, stops: (f?.reach ?? []).filter((r) => r.stand && !(r.tile.x === f!.from.x && r.tile.y === f!.from.y)).map((r) => ({ tile: r.tile, cost: r.cost })).sort((a, b) => a.cost - b.cost) };
        }
        return stopsCache.stops;
      },
      standsAt: (heroId, slot) => {
        const h = get().snapshot;
        const unit = snapshot.units.find((u) => u.heroId === heroId)?.unitId;
        if (!bridge || !h || unit === undefined) return [];
        // keyed by the plan as `legal` reads it (local edits included), not the last rAF projection
        const merged = get().mergedIntents();
        const plan = merged.map((i) => `${intentKey(i)}@${i.revision}`).sort().join(',');
        const key = `${h.id}:${h.hash}:${plan}:${heroId}:${slot}`;
        let hit = standsCache.get(key);
        if (!hit) {
          if (standsCache.size > 64) standsCache = new Map();
          hit = bridge.stands(h, unit, slot, merged.map(({ state: _s, owner: _o, rowId: _r, ...i }) => i));
          standsCache.set(key, hit);
        }
        return hit;
      },
      // the ground's own name, from any hero's survey (the classes are the board's; only the prices are the unit's)
      groundAt: (tile) => {
        // docs/139 WD5: the survey takes a never-seen square for open floor; nothing is said of it
        if (neverSeen(snapshot.fog, tile)) return undefined;
        const h = get().snapshot;
        const unit = snapshot.units.find((u) => u.heroId !== undefined && u.onBoard !== false)?.unitId;
        if (!bridge || !h || unit === undefined) return undefined;
        const key = `${h.id}:${h.hash}:${unit}`;
        if (groundCache?.key !== key) groundCache = { key, field: bridge.survey(h, unit, 1) };
        const f = groundCache.field;
        const c = f ? fieldClassAt(f, tile, f.w) : undefined;
        // a puzzle room keeps plain rules on a drawn wood (docs/57): its walls are what the kit draws
        const name = c?.key === 'wall' ? WALL_DRAWN[snapshot.board.kit ?? ''] ?? c.label : c?.label;
        // docs/66 RM15: a square above its neighbours says what the height gives
        const t = snapshot.board.tiles[tile.y * snapshot.board.w + tile.x];
        const ground = name && heightOf(t) > 0 && c?.key !== 'wall' ? `${name} · ${HIGH_GROUND_WORDS}` : name;
        // docs/114 MT8: a thing standing there is what the square is; a spill or a cloud lies on its ground
        const what = describeSquare(snapshot, tile);
        if (what && thingAt(snapshot, tile)) return what;
        return what ? (ground ? `${what} · ${ground}` : what) : ground;
      },
      unknown: (tile) => unknownSquare(snapshot.fog, tile),
    };
    machine = new AuthoringMachine(ctx, { w: snapshot.board.w, h: snapshot.board.h });
    machine.onOutput(handleOutput);
    // docs/46 T3: every turn opens with no hero selected — the player picks whom to plan for first;
    // only a restage within the same pulse keeps the hero they were on. A player with one hero
    // has no choice to make, so that hero is picked for them (quietly: no walk field yet).
    if (keepHero !== undefined && controlled.includes(keepHero)) machine.send({ type: 'selectHero', heroId: keepHero });
    else {
      const free = ctx.heroes().filter((h) => !ctx.committed(h.heroId));
      const only = controlled.length === 1 && free.length === 1 ? free[0] : undefined;
      if (only) machine.send({ type: 'selectHero', heroId: only.heroId });
      set({ autoSelected: !!only });
    }
  };

  return {
    pendingArgs: {}, local: new Map(), rejected: new Map(), projectedAt: 0, dirty: false,
    machineState: { kind: 'Idle' }, cursor: { x: 0, y: 0 }, hotbar: [], announcement: '', me: '', controlled: [], rows: new Map(), routes: [], autoSelected: false, struck: { seq: 0, items: [] },

    attach(b, handle, snapshot, me, controlled, encounterId, hashBytes) {
      bridge = b;
      snapshotData = snapshot;
      void hashBytes;   // the commit it was for is now the server's Lock plan (`ready`)
      const keep = get().encounterId === encounterId && get().pulseIndex === snapshot.pulseIndex ? get().selectedHero : undefined;
      // A new pulse (snapshot hash change) clears local and rejected entirely (11 §11.3).
      unsent.clear();
      set({ snapshot: handle, snapshotHash: snapshot.snapshotHash, pulseIndex: snapshot.pulseIndex, encounterId, me, controlled, local: new Map(), rejected: new Map(), dirty: true, projected: undefined });
      set({ rows: currentRows(snapshot.snapshotHash, snapshot.pulseIndex) });
      buildMachine(snapshot, controlled, keep);
      syncFromMachine();
      get().recompute();
    },
    detach() {
      machine = undefined; snapshotData = undefined; serverIntents = []; unsent.clear();
      set({ snapshot: undefined, snapshotHash: undefined, encounterId: undefined, projected: undefined, local: new Map(), rejected: new Map(), rows: new Map(), controlled: [], machineState: { kind: 'Idle' }, selectedHero: undefined, slot: undefined, hotbar: [] });
    },
    setControlled(controlled) {
      const prev = get().controlled;
      if (prev.length === controlled.length && prev.every((h, i) => h === controlled[i])) return;
      set({ controlled });
      if (snapshotData) { buildMachine(snapshotData, controlled, get().selectedHero); syncFromMachine(); }
    },
    serverRoutes(routes) {
      routeRows = routes;
      get().recompute();
    },
    serverChanged(intents) {
      serverIntents = intents;
      const rows = currentRows(get().snapshotHash, get().pulseIndex);
      // Arrival of a row with a revision ≥ ours drops the local entry (11 §11.3).
      const local = new Map(get().local);
      for (const [k, l] of local) { const r = rows.get(k); if (r && r.revision >= l.revision) local.delete(k); }
      const struck = struckRows(get().rows, rows);
      set({ rows, local, dirty: true });
      if (struck.length) {
        set({ struck: { seq: get().struck.seq + 1, items: struck } });
        // docs/81 SA5: the change is heard and seen (the scene draws the whoosh), and said
        playSfx('plan.struck');
        const name = (h: number) => snapshotData?.units.find((u) => u.heroId === h)?.name ?? 'A hero';
        set({ announcement: struck.map((x) => `${name(x.heroId)}'s ${x.slot === 'B' ? 'bonus' : x.slot === 1 ? '1st' : '2nd'} action no longer works — taken back`).join(' · ') });
      }
      get().recompute();
    },

    send(ev) {
      if (!machine) return;
      // while the last turn plays out, the plan is hidden: nothing is planned until the next planning
      // phase opens (a click or key then would plan a turn the player cannot see); the cursor still follows
      if (usePlayback.getState().playing && ev.type !== 'hover' && ev.type !== 'moveCursor') return;
      // docs/34: picking up a hero, a slot or an action lifts a card (the place itself sounds in handleOutput)
      if (ev.type === 'selectHero' || ev.type === 'chooseSlot' || ev.type === 'chooseAction') playSfx('plan.select', { gain: 0.8 });
      if (ev.type !== 'hover' && ev.type !== 'moveCursor' && get().autoSelected) set({ autoSelected: false });
      machine.send(ev); syncFromMachine();
    },
    select(heroId) { get().send({ type: 'selectHero', ...(heroId !== undefined ? { heroId } : {}) }); },
    chooseSlot(s) { get().send({ type: 'chooseSlot', slot: s }); },
    chooseAction(a) { const def = get().hotbar.find((h) => h.id === a) ?? actionById(a); if (def) get().send({ type: 'chooseAction', action: def }); },
    setArg(k, v) {
      if (!machine) return { legal: false, reason: 'Encounter' };
      if (k === 'tile') { machine.send({ type: 'hover', tile: v as Tile }); syncFromMachine(); return machine.probe?.verdict ?? { legal: true, cost: 0 }; }
      if (k === 'unit') { get().send({ type: 'pickUnit', unit: v as number }); return { legal: true, cost: 0 }; }
      if (k === 'dir') { get().send({ type: 'pickDirection', dir: v as never }); return { legal: true, cost: 0 }; }
      return { legal: false, reason: 'Requires' };
    },
    place() { get().send({ type: 'confirm' }); },
    clear(heroId, slot) { get().send({ type: 'clear', heroId, slot }); },
    hoverTile(tile) { get().send({ type: 'hover', tile }); },
    clickTile(tile) {
      // a click on the hero picked for the player is them picking it: show its walk field, keep it
      const st = get().machineState;
      if (get().autoSelected && st.kind === 'HeroSelected' && snapshotData?.units.find((u) => covers(u, tile) && u.onBoard !== false)?.heroId === st.heroId) { get().send({ type: 'selectHero', heroId: st.heroId }); return; }
      get().send({ type: 'click', tile });
    },
    barActions() {
      const heroId = get().selectedHero;
      return barFor(heroId === undefined ? undefined : snapshotData?.units.find((u) => u.heroId === heroId), heroId !== undefined);
    },
    pickAction(id) {
      const { slot } = get();
      const to = get().slotFor(id);
      if (to === undefined) return;
      if (to !== slot) get().chooseSlot(to);
      get().chooseAction(id);
    },

    slotFor(id) {
      const { slot, selectedHero: heroId, local, rows } = get();
      const def = actionById(id);
      if (heroId === undefined || !def) return undefined;
      if (slot !== undefined && def.slots.includes(slot)) return slot;
      return SLOTS.find((s) => def.slots.includes(s) && !local.has(`${heroId}:${s}`) && !rows.has(`${heroId}:${s}`)) ?? SLOTS.find((s) => def.slots.includes(s));
    },

    changeGear(heroId) {
      const def = actionById(CHANGE_GEAR);
      if (!def) return;
      const held = get().local.get(`${heroId}:B`) ?? get().rows.get(`${heroId}:B`);
      if (held?.action === CHANGE_GEAR) return;
      get().send({ type: 'planBonus', heroId, action: def });
    },

    useItem(item) {
      const heroId = get().selectedHero;
      const hero = heroId === undefined ? undefined : snapshotData?.units.find((u) => u.heroId === heroId);
      const row = snapshotData?.bags?.find((b) => b.unit === hero?.unitId)?.items.find((i) => i.item === item);
      if (!hero || !row) return;
      get().send({ type: 'useItem', item, friend: !!consumable(row.def)?.reachesAFriend });
    },

    itemVerdict(item) {
      const heroId = get().selectedHero;
      const hero = heroId === undefined ? undefined : snapshotData?.units.find((u) => u.heroId === heroId);
      const h = get().snapshot;
      if (!bridge || !h || !hero || !snapshotData) return { legal: false, reason: 'Encounter' };
      if ([...get().rows.values()].some((i) => i.heroId === heroId && i.state === 'committed')) return { legal: false, reason: 'Committed' };
      return bridge.legal(h, { heroId: heroId!, unit: hero.unitId, slot: 'B', action: 6, args: { kind: 'item', item }, snapshotHash: snapshotData.snapshotHash, revision: 0, substituted: false });
    },

    mergedIntents() {
      // §11.3: a local entry for (hero, slot) shadows the server's row for the same key.
      const merged = new Map<string, IntentRow>(get().rows);
      for (const [k, l] of get().local) merged.set(k, { ...l, state: 'local', owner: get().me });
      return [...merged.values()];
    },
    snapshotData() { return snapshotData; },
    stats() { return bridge?.stats(); },

    recompute() {
      if (rafHandle !== undefined) cancel(rafHandle);
      rafHandle = schedule(() => {
        rafHandle = undefined;
        const { snapshot } = get();
        if (!bridge || !snapshot) return;
        const merged = get().mergedIntents();
        const states = new Map<string, IntentState>(merged.map((i) => [intentKey(i), i.state]));
        for (const [k] of get().rejected) states.set(k, 'rejected');
        let projected: ProjectedBoard;
        try { projected = applyStates(bridge.project(snapshot, merged.map(({ state: _s, owner: _o, rowId: _r, ...i }) => i)), states); }
        catch (err) { console.error('[authoring] project failed', err); return; }
        const routes = routeRows.flatMap((r): RouteTag[] => {
          const unit = snapshotData?.units.find((u) => u.heroId === r.heroId)?.unitId;
          const legs = unit === undefined ? undefined : bridge!.routeLegs(snapshot, unit, { x: r.x, y: r.y });
          return legs ? [{ heroId: r.heroId, legs }] : [];
        });
        set({ projected, projectedAt: deviceNow(), dirty: false, routes });
        // the plan moved under the cursor: read the square afresh (docs/33 E1)
        if (machine) { machine.invalidate(); syncFromMachine(); }
      });
    },
  };
});

export { SLOTS };
